import { call } from '@packages/functions';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

interface IntersectOptions extends IntersectionObserverInit {
    quickRelease: boolean;
    delay?: number;
    onIntersect?: (entry?: IntersectionObserverEntry) => void;
}

interface IntersectionAPI<T> {
    element: MutableRefObject<T | null>;
    intersection: IntersectionObserverEntry | null;
    observer: MutableRefObject<IntersectionObserver | null>;
    isIntersected: boolean;

    unobserve(): void;

    observe(el: any): void;
}

export function useIntersect<T = HTMLElement>({
    quickRelease = false,
    delay = 300,
    onIntersect,
    ...rest
}: IntersectOptions): IntersectionAPI<T> {
    const [intersection, setIntersection] = useState<IntersectionObserverEntry | null>(null);
    const element = useRef<T | null>(null);

    const observer = useRef<IntersectionObserver | null>(
        new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (quickRelease && entry.isIntersecting) {
                    observer.current?.unobserve(entry.target);
                    observer.current.disconnect();
                }

                call(onIntersect, entry);

                if (entry.target === (element.current as any)) {
                    setIntersection(entry);
                }
            }, rest);
        }),
    );

    const unobserve = useCallback(() => {
        if (element.current) {
            observer.current?.unobserve(element.current as any);
        }
    }, [element.current, observer.current]);

    const observe = useCallback(
        (element) => {
            observer.current?.observe(element);
        },
        [observer.current],
    );

    useEffect(() => {
        if (!element.current) {
            return;
        }

        let delayTimeout: any;
        if (supportIntersectionObserver()) {
            delayTimeout = setTimeout(() => {
                if (element.current) {
                    observe(element.current as any);
                }
            }, delay);
        }

        return () => {
            observer.current?.disconnect();
            if (delayTimeout) {
                clearTimeout(delayTimeout);
            }
        };
    }, []);

    return {
        element,
        intersection,
        observer,
        observe,
        unobserve,
        isIntersected: !!intersection?.isIntersecting,
    };
}

/**
 * Check if support intersection observer
 * @returns {boolean}
 */
export const supportIntersectionObserver = () =>
    typeof window !== 'undefined' &&
    window.hasOwnProperty('IntersectionObserver') &&
    typeof IntersectionObserver !== 'undefined';
